import { driver, type DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'
import '@/styles/tour.scss'
import { TOURS, type FeatureTour, type TourBadge } from '@/constants/tours'
import { useI18n } from 'vue-i18n'
import { useRtl } from 'vuetify'

const STORAGE_PREFIX = 'tour_'

function getStorageKey(tourId: string, suffix: string) {
  return `${STORAGE_PREFIX}${tourId}_${suffix}`
}

export function useFeatureTour() {
  const { t } = useI18n()
  const { isRtl } = useRtl()

  function isTourSeen(tourId: string): boolean {
    return localStorage.getItem(getStorageKey(tourId, 'seen')) === 'true'
  }

  function markTourSeen(tourId: string) {
    localStorage.setItem(getStorageKey(tourId, 'seen'), 'true')
  }

  function getActiveTourForPage(page: string): FeatureTour | null {
    return (
      TOURS.filter(
        tour => tour.active && tour.page === page && !isTourSeen(tour.id)
      ).sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99))[0] ?? null
    )
  }

  function getBadgesForPage(page: string): (TourBadge & { tourId: string })[] {
    return TOURS.filter(
      tour => tour.active && tour.badges?.length && !isTourSeen(tour.id)
    ).flatMap(tour =>
      (tour.badges ?? []).map(badge => ({ ...badge, tourId: tour.id }))
    )
  }

  function isBadgeVisible(selector: string): boolean {
    return TOURS.some(
      tour =>
        tour.active &&
        !isTourSeen(tour.id) &&
        tour.badges?.some(b => b.selector === selector)
    )
  }

  function startTour(tourId: string) {
    const tour = TOURS.find(t => t.id === tourId)
    if (!tour || isTourSeen(tourId)) return

    const rtl = isRtl.value

    const flipSide = (side: string) => {
      if (!rtl) return side
      if (side === 'left') return 'right'
      if (side === 'right') return 'left'
      return side
    }

    const steps: DriveStep[] = tour.steps
      .filter(step => document.querySelector(step.selector))
      .map(step => ({
        element: step.selector,
        popover: {
          title: t(step.title),
          description: t(step.description),
          side: flipSide(step.side ?? 'bottom') as any,
          align: 'center' as const,
        },
      }))

    if (!steps.length) return

    const driverObj = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      stagePadding: 8,
      stageRadius: 10,
      popoverClass: rtl ? 'driver-popover-rtl' : '',
      nextBtnText: t('tour.nextBtn'),
      prevBtnText: t('tour.prevBtn'),
      doneBtnText: t('tour.doneBtn'),
      progressText: `{{current}} / {{total}}`,
      steps,
      onDestroyStarted: () => {
        markTourSeen(tourId)
        driverObj.destroy()
      },
    })

    driverObj.drive()
  }

  return {
    isTourSeen,
    markTourSeen,
    getActiveTourForPage,
    getBadgesForPage,
    isBadgeVisible,
    startTour,
  }
}
